import { buildMetaTags } from "@fscrypto/domain";
import type { UserWalletWithWalletAndChain } from "@fscrypto/domain/earn";
import { sortJourneysByPriority, sortQuestsByPriority } from "@fscrypto/domain/earn";
import type { Partner } from "@fscrypto/domain/partner";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { BookOpenIcon, TrophyIcon } from "lucide-react";
import { createRequestCaller } from "~/.server/router";
import { Faq } from "~/components/faq-section";
import { JourneyList } from "~/components/journey-list/journey-list";
import { JourneyListCard } from "~/components/journey-list/journey-list-card";
import { PartnerHeader } from "~/components/partner-header";
import { QuestCard } from "~/components/quest-card";
import { QuestTableNoRewards } from "~/components/quest-table-no-rewards";
import { json, useLoaderData } from "~/remix";

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const slug = params.partner;
  if (!slug) throw json("slug is missing", { status: 500 });

  const caller = await createRequestCaller(request);

  const [journeysUnsorted, partner, partners, questListItems, user] = await Promise.all([
    caller.public.journeys.byPartner({ partnerSlug: slug }),
    caller.public.partners.bySlug(slug), // gets the partner with chains
    caller.public.partners.getQuestPartners(),
    caller.public.quests.standalone({ partner: slug }),
    caller.public.me(),
  ]);
  if (!partner) throw json("partner not found", { status: 404 });

  const questListItemsWithRewards = sortQuestsByPriority(questListItems.filter((quest) => quest.isRewardActive));
  const questListItemsWithoutRewards = sortQuestsByPriority(questListItems.filter((quest) => !quest.isRewardActive));
  const journeys = sortJourneysByPriority(journeysUnsorted);
  const partnerScoreChain = partner.partnerChains.find((c) => c.type === "SCORES");

  let userWallets: UserWalletWithWalletAndChain[] = [];
  let scoreWallet: UserWalletWithWalletAndChain | undefined = undefined;

  if (user?.id) {
    userWallets = await caller.public.userWallets.all();
    if (partnerScoreChain?.chainId) {
      scoreWallet = userWallets.find((w) => w.wallet.chain.id === partnerScoreChain.chainId) ?? undefined;
    }
  }

  return json({
    questListItemsWithRewards,
    questListItemsWithoutRewards,
    journeys,
    partner,
    partners,
    scoreChain: partnerScoreChain?.chain,
    scoreWallet,
  });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const partner = data.partner as Partner;
  const slug = partner.slug;
  const displayName = partner.displayName;
  const title = `${displayName} Quests | Earn Crypto with Flipside`;
  const description = `Complete ${displayName} quests, earn tokens, and explore the ${displayName} ecosystem. Join Flipside's community of crypto explorers to shape the onchain economy.`;
  const url = `https://flipsidecrypto.xyz/earn/${slug}`;

  return buildMetaTags({
    title,
    description,
    canonicalUrl: url,
    ogImageUrl: "https://res.cloudinary.com/dsr37ut2z/image/upload/v1731516801/assets/og-images/Earn_1.png",
    twitterImageUrl: "https://res.cloudinary.com/dsr37ut2z/image/upload/v1731516801/assets/og-images/Earn_2.png",
  });
};

export default function Index() {
  const {
    questListItemsWithRewards,
    journeys,
    partner,
    partners,
    scoreChain,
    questListItemsWithoutRewards,
    scoreWallet,
  } = useLoaderData<typeof loader>();

  return (
    <main className="container max-w-screen-2xl">
      <PartnerHeader partners={partners} partner={partner} chain={scoreChain} scoreWallet={scoreWallet} />

      {journeys.length === 0 && questListItemsWithRewards.length === 0 && questListItemsWithoutRewards.length === 0 && (
        <div className="py-20">
          <p className="text-md text-gray-400/70">
            No quests or journeys found. Check back soon for new opportunities!
          </p>
        </div>
      )}
      {journeys.length > 0 && (
        <JourneyList>
          {journeys.map((journey) => (
            <JourneyListCard key={journey.id} journey={journey} />
          ))}
        </JourneyList>
      )}

      {questListItemsWithRewards.length > 0 && (
        <>
          <div className="flex items-center gap-2 pt-8 pb-4">
            <TrophyIcon className="text-primary/80 size-5" />
            <h2 className="text-2xl font-normal tracking-wide text-foreground/90 border-b border-primary/20 pb-1">
              Quests with Rewards
            </h2>
          </div>
          <p className="text-md text-gray-400/70 mb-4">
            Complete these quests to earn tokens and other rewards. Each quest offers unique challenges and
            opportunities to earn while learning about different protocols.
          </p>
          <div className="grid grid-cols-4 gap-6 py-6">
            {questListItemsWithRewards.map((quest) => (
              <QuestCard key={quest.id} questListItem={quest} />
            ))}
          </div>
        </>
      )}

      {questListItemsWithoutRewards.length > 0 && (
        <>
          <div className="flex items-center gap-2 pt-8 pb-4">
            <BookOpenIcon className="text-primary/80 size-5" />
            <h2 className="text-2xl font-normal tracking-wide text-foreground/90 border-b border-primary/20 pb-1">
              Quests without Rewards
            </h2>
          </div>
          <p className="text-md text-gray-400/70">
            Quests that do not have a reward. You can still participate in these quests to increase your onchain scores!
          </p>
          <div className="py-4">
            <QuestTableNoRewards questListItems={questListItemsWithoutRewards} />
          </div>
        </>
      )}
      <Faq />
    </main>
  );
}
